/**
 * Text-to-Speech Service using Web Speech API
 * Supports multiple languages with offline capability
 */

export type TTSLanguage = 'cs-CZ' | 'ar-SA' | 'en-US' | 'de-DE' | 'fr-FR' | 'es-ES';

export interface TTSOptions {
  lang?: TTSLanguage;
  rate?: number; // 0.1 to 10, default 1
  pitch?: number; // 0 to 2, default 1
  volume?: number; // 0 to 1, default 1
}

class TextToSpeechService {
  private synth: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
    }
  }

  /**
   * Check if TTS is supported
   */
  isSupported(): boolean {
    return this.synth !== null;
  }

  /**
   * Get available voices
   */
  async getVoices(): Promise<SpeechSynthesisVoice[]> {
    if (!this.synth) return [];

    return new Promise((resolve) => {
      let voices = this.synth!.getVoices();

      if (voices.length > 0) {
        resolve(voices);
      } else {
        // Voices might load asynchronously
        this.synth!.addEventListener('voiceschanged', () => {
          voices = this.synth!.getVoices();
          resolve(voices);
        });
      }
    });
  }

  /**
   * Get best voice for language
   */
  async getVoiceForLanguage(lang: TTSLanguage): Promise<SpeechSynthesisVoice | null> {
    const voices = await this.getVoices();

    // Try to find a voice for the specific language
    let voice = voices.find((v) => v.lang === lang);

    // Fallback to language prefix (e.g., 'ar' for 'ar-SA')
    if (!voice) {
      const langPrefix = lang.split('-')[0];
      voice = voices.find((v) => v.lang.startsWith(langPrefix));
    }

    return voice || null;
  }

  /**
   * Speak text
   */
  async speak(text: string, options: TTSOptions = {}): Promise<void> {
    if (!this.synth || !text.trim()) {
      console.warn('TTS not supported or empty text');
      return;
    }

    // Cancel any ongoing speech safely
    try {
      if (this.synth.speaking) {
        this.synth.cancel();
      }
    } catch (error) {
      console.warn('Could not cancel previous speech:', error);
    }

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      this.currentUtterance = utterance;

      // Set options
      utterance.lang = options.lang || 'cs-CZ';
      utterance.rate = options.rate ?? 1.0;
      utterance.pitch = options.pitch ?? 1.0;
      utterance.volume = options.volume ?? 1.0;

      // Get appropriate voice
      this.getVoiceForLanguage(utterance.lang as TTSLanguage).then((voice) => {
        if (voice) {
          utterance.voice = voice;
        }

        // Event handlers
        utterance.onend = () => {
          this.currentUtterance = null;
          resolve();
        };

        utterance.onerror = (event) => {
          this.currentUtterance = null;
          console.error('TTS error:', event);
          reject(event);
        };

        // Speak
        try {
          this.synth!.speak(utterance);
        } catch (error) {
          console.error('Error speaking:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Cancel current speech
   */
  cancel(): void {
    try {
      if (this.synth) {
        this.synth.cancel();
        this.currentUtterance = null;
      }
    } catch (error) {
      console.warn('Error cancelling speech:', error);
      this.currentUtterance = null;
    }
  }

  /**
   * Pause speech
   */
  pause(): void {
    try {
      if (this.synth && this.synth.speaking) {
        this.synth.pause();
      }
    } catch (error) {
      console.warn('Error pausing speech:', error);
    }
  }

  /**
   * Resume speech
   */
  resume(): void {
    try {
      if (this.synth && this.synth.paused) {
        this.synth.resume();
      }
    } catch (error) {
      console.warn('Error resuming speech:', error);
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.synth?.speaking || false;
  }
}

// Singleton instance
export const ttsService = new TextToSpeechService();

// Map ISO 639-1 language codes to TTS language codes
const LANGUAGE_CODE_MAP: Record<string, TTSLanguage> = {
  cs: 'cs-CZ',
  ar: 'ar-SA',
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
};

/**
 * Helper function for flashcard TTS
 * @param text - Text to speak
 * @param languageCode - ISO 639-1 language code (e.g., 'cs', 'ar', 'en')
 * @param rate - Speech rate (default: 1.0)
 */
export async function speakFlashcard(
  text: string,
  languageCode: string,
  rate: number = 1.0
): Promise<void> {
  // Normalize language code to lowercase and get first part (e.g., 'en-US' -> 'en')
  const normalizedCode = languageCode.toLowerCase().split('-')[0];

  // Get TTS language code, fallback to cs-CZ if not found
  const lang: TTSLanguage = LANGUAGE_CODE_MAP[normalizedCode] || 'cs-CZ';

  await ttsService.speak(text, {
    lang,
    rate,
    pitch: 1.0,
    volume: 1.0,
  });
}
